import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, Divider, Grid } from '@mui/material';
import { Timeline as TimelineIcon } from '@mui/icons-material';
import { DateRangeSlider } from '../components/DateRangeSlider';
import ProfileInfoTable from '../components/ProfileInfoTable';
import { DroneProfilePicture } from '../components/DroneProfilePicture';
import { TechnicalSpecifications } from '../components/TechnicalSpecifications';
import { DashboardData, DashboardFilters } from '../types/dashboard.types';
import { useDateRangeSlider } from '../hooks/useDateRangeSlider';
import { NotesListView, SimpleGanttChart } from './statistics';
import { useNotesHistory } from './statistics/hooks';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { UserProfile } from '../../../lib/auth-state-machine';
import { useDroneStatusQuery } from './profile.queries';
import { useFetchFlightsForPlottingMutation } from './profile.mutations';
import { DroneStatus } from '../../../types/api';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useTranslation } from '../../../i18n';
import { flightHandoffEntry } from '../../../utils/flightFileRefs';
import { sharedCountersNote } from '../../../utils/sharedFlights';
// Same-origin handoff (#617): the Analytics SPA reads org + auth from the JWT in
// shared localStorage, so only the `#s3Keys=` fragment travels by URL. The
// encoding and the `noopener,noreferrer` window options live in the shared
// helper, which entry points 2, 3 and 4 all now call — see docs/PLOTTING.md.
import { openAnalyticsWithFiles } from '../../file-browser/utils';

interface ProfileTabProps {
  data: DashboardData;
  filters: DashboardFilters;
  flightDates: string[];
  onFilterChange: (filters: Partial<DashboardFilters>) => void;
  profile?: UserProfile | null;
  onRefreshData?: () => void;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({
  data,
  filters,
  flightDates,
  onFilterChange,
  profile,
  onRefreshData,
}) => {
  const { t } = useTranslation();
  const { stats, droneStats, dateRange } = data;
  const { getSliderDateRange } = useDateRangeSlider(dateRange, flightDates);
  const { showError, showSuccess } = useErrorHandler();

  // Drone status via TanStack Query (STD-015)
  const droneStatusQuery = useDroneStatusQuery(filters.selectedSerial);
  const fetchFlightsForPlotting = useFetchFlightsForPlottingMutation();
  const [droneStatus, setDroneStatus] = useState<DroneStatus | null>(null);
  const [dronePictureUrl, setDronePictureUrl] = useState<string | null>(null);
  const [localMetadata, setLocalMetadata] = useState<Record<string, unknown>>({});

  // Notes state for interaction between timeline and notes list
  const [highlightedNoteId, setHighlightedNoteId] = useState<string | null>(null);

  // Fetch all notes for timeline (without date filtering)
  const { notes: allNotes } = useNotesHistory({
    droneSerial: filters.selectedSerial,
    includeResolved: true,
  });

  // Handler for when a note marker is clicked on the timeline
  const handleTimelineNoteClick = useCallback((noteId: string) => {
    setHighlightedNoteId(noteId);
    // Scroll to note in the list after a short delay to allow the list to update
    setTimeout(() => {
      const noteElement = document.querySelector(`[data-note-id="${noteId}"]`);
      if (noteElement) {
        noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  // Handler for when the timeline year changes - clear highlight when navigating
  const handleTimelineYearChange = useCallback(() => {
    setHighlightedNoteId(null);
  }, []);

  // Get current drone from summary data
  const getCurrentDrone = useCallback(() => {
    if (!filters.selectedSerial || !data.allDronesSummary?.drones) {
      return null;
    }

    return (
      data.allDronesSummary.drones.find(
        (drone) => drone.serial_number === filters.selectedSerial
      ) || null
    );
  }, [filters.selectedSerial, data.allDronesSummary]);

  // Get current drone's picture URL from data
  const getCurrentDronePictureUrl = useCallback((): string | null => {
    return getCurrentDrone()?.picture_url || null;
  }, [getCurrentDrone]);

  // Sync drone status from query into local state (local state allows handleStatusUpdate overrides)
  useEffect(() => {
    if (droneStatusQuery.data !== undefined) {
      setDroneStatus(droneStatusQuery.data);
    } else if (!filters.selectedSerial) {
      setDroneStatus(null);
    }
  }, [droneStatusQuery.data, filters.selectedSerial]);

  // Update picture URL and metadata when data changes
  useEffect(() => {
    const newPictureUrl = getCurrentDronePictureUrl();
    setDronePictureUrl(newPictureUrl);

    // Sync local metadata with droneStats
    if (droneStats?.metadata) {
      setLocalMetadata(droneStats.metadata);
    }
  }, [data.allDronesSummary, filters.selectedSerial, droneStats, getCurrentDronePictureUrl]);

  // Handle drone status updates
  const handleStatusUpdate = (newStatus: DroneStatus) => {
    setDroneStatus(newStatus);
  };

  // Handle drone picture updates
  const handlePictureUpdate = (newPictureUrl: string) => {
    setDronePictureUrl(newPictureUrl);
  };

  // Handle metadata updates
  const handleMetadataUpdate = (newMetadata: Record<string, unknown>) => {
    setLocalMetadata(newMetadata);
  };

  const handlePlotFlightData = async () => {
    if (!filters.selectedSerial) {
      return;
    }

    try {
      // Get filtered date range
      const { start, end } = getSliderDateRange(filters.sliderValues, flightDates);

      // Use optimized API to get flights directly from database
      const flightsResponse = await fetchFlightsForPlotting.mutateAsync({
        serial: filters.selectedSerial,
        start,
        end,
      });

      if (!flightsResponse.success) {
        showError('Failed to retrieve flights for plotting');
        return;
      }

      if (flightsResponse.flights.length === 0) {
        showError('No flights found for selected date range');
        return;
      }

      // **This is an ADDRESS, not a label, and #39 Phase C changed what a valid
      // one looks like.** It used to be built entirely by editing the storage
      // key: strip `organizations/<slug>/`, then pop the last segment for a
      // display name. Both steps assumed the key was a path built from a
      // filename. A Phase-C key is a minted uuid, so the strip is a no-op, the
      // "filename" is a uuid, and `/api/s3/download-csv` — which resolves a
      // *display path* and refuses anything not ending in `.csv` — answers "File
      // must be a csv file" for every flight uploaded after Phase C.
      //
      // So the id leads. `object_id` is the `fleet.file_objects` row, which
      // `handoffRefsFromPayload` already prefers over a path (#1231/#1242) and
      // which resolves through `/file-objects/{id}/download` with no path
      // arithmetic anywhere. It is also an address that cannot be *edited* into
      // another organization's, which is the bug #1231 closed on the agent side.
      //
      // The path arm stays for flights with no `object_id` — nullable by design —
      // and only those. It keeps the prefix strip, because for such a row the key
      // really is a path in the caller's own tree.
      // One shared pure helper (`utils/flightFileRefs`), tested in
      // `utils/__tests__/flightFileRefs.test.ts`. Extracted rather than inlined
      // because this is the assertion `tsc` cannot make — `object_id` and
      // `s3_key` are both `string`, so preferring the wrong one type-checks — and
      // this app has no jsdom to reach it through the component.
      //
      // Handing off through `openAnalyticsWithFiles` rather than re-implementing
      // the base64 + `window.open` here (#1297): this surface and the Flight
      // Details dialog now build the entry AND open the tab through the same two
      // functions, so neither the payload nor the window options can drift. Doing
      // that was only safe once the shared helper stopped re-deriving `filename`
      // from the key — until then routing this call through it would have
      // reintroduced the very clobber the entry above exists to avoid.
      const orgSlug = profile?.organization?.slug ?? null;
      openAnalyticsWithFiles(
        flightsResponse.flights.map((flight) => flightHandoffEntry(flight, orgSlug)),
        orgSlug
      );
      showSuccess('Flight plotter opened successfully');
    } catch (error) {
      showError(error, 'Failed to plot flight data');
    }
  };

  if (!stats && !droneStats) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          {t('profileTab.selectDrone')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Date Range Slider with integrated plot button */}
      {filters.selectedSerial && dateRange && (
        <Box sx={{ mb: 2 }}>
          <DateRangeSlider
            dateRange={dateRange}
            sliderValues={filters.sliderValues}
            onSliderChange={(_event, value, _activeThumb) => {
              onFilterChange({ sliderValues: value as number[] });
            }}
            flightDates={flightDates}
            actionButton={
              <Button
                variant="outlined"
                endIcon={<TimelineIcon />}
                onClick={handlePlotFlightData}
                size="small"
                sx={{
                  borderColor: BRAND_COLORS.yellow.main,
                  color: BRAND_COLORS.yellow.main,
                  height: '32px',
                  minWidth: 'auto',
                  px: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  '& .MuiButton-endIcon': {
                    marginLeft: { xs: '4px', sm: '6px' },
                    '& svg': {
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                    },
                  },
                  '&:hover': {
                    borderColor: BRAND_COLORS.yellow.main,
                    backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.04),
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }}
                >
                  {t('profileTab.plotData')}
                </Box>
              </Button>
            }
          />
        </Box>
      )}

      {/* Drone Information Section */}
      {filters.selectedSerial && (
        <Box sx={{ mt: 2 }}>
          {/* Section Header */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 'bold',
              color: 'text.primary',
            }}
          >
            {t('profileTab.droneInformation')}
          </Typography>

          {/* Drone Profile Picture and Statistics Table */}
          <Grid container spacing={3}>
            {/* Drone Profile Picture */}
            <Grid item xs={12} md={5} lg={4}>
              <DroneProfilePicture
                droneSerial={filters.selectedSerial}
                pictureUrl={dronePictureUrl}
                onPictureUpdate={handlePictureUpdate}
                onDataRefresh={onRefreshData}
              />
            </Grid>

            {/* Profile Information Table */}
            <Grid item xs={12} md={7} lg={8}>
              <ProfileInfoTable
                totalFiles={
                  filters.selectedSerial && droneStats
                    ? droneStats.files_processed
                    : stats?.total_files || 0
                }
                engineHours={droneStats?.total_engine_hours}
                flightHours={droneStats?.total_flight_hours}
                engineStarts={droneStats?.total_engine_starts}
                firstFlight={droneStats?.first_flight_date || undefined}
                firstEngineRun={droneStats?.first_engine_run || undefined}
                droneStatus={droneStatus}
                droneSerial={filters.selectedSerial}
                onStatusUpdate={handleStatusUpdate}
                onDataRefresh={onRefreshData}
                legacyHours={getCurrentDrone()?.legacy_hours}
                dateFiltered={filters.sliderValues[0] !== 0 || filters.sliderValues[1] !== 100}
                organizationName={getCurrentDrone()?.organization_name}
                ownershipType={getCurrentDrone()?.ownership_type}
                leaseEndDate={getCurrentDrone()?.lease_end_date}
                sharedNote={sharedCountersNote(
                  t,
                  droneStats?.shared_flights,
                  droneStats?.shared_by
                )}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* General Profile (when no drone selected) */}
      {!filters.selectedSerial && (
        <Box sx={{ mt: 2 }}>
          <ProfileInfoTable
            totalFiles={stats?.total_files || 0}
            engineHours={undefined}
            engineStarts={undefined}
            firstFlight={undefined}
            firstEngineRun={undefined}
            droneStatus={null}
            droneSerial={undefined}
            onStatusUpdate={handleStatusUpdate}
            onDataRefresh={onRefreshData}
          />
        </Box>
      )}

      {/* Technical Specifications Section */}
      {filters.selectedSerial && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <TechnicalSpecifications
            droneSerial={filters.selectedSerial}
            metadata={localMetadata}
            onDataRefresh={onRefreshData}
            onMetadataUpdate={handleMetadataUpdate}
          />
        </Box>
      )}

      {/* Notes Section - Shows all notes (not filtered by timeline year) */}
      {filters.selectedSerial && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <NotesListView
            droneSerial={filters.selectedSerial}
            highlightedNoteId={highlightedNoteId}
            initialNotes={allNotes}
          />
        </Box>
      )}

      {/* Timeline Section */}
      {filters.selectedSerial &&
        dateRange &&
        dateRange.flight_dates &&
        dateRange.flight_dates.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <SimpleGanttChart
              flightDates={dateRange.flight_dates}
              sharedFlightDates={droneStats?.shared_flight_dates}
              droneSerial={filters.selectedSerial}
              notes={allNotes.map((n) => ({
                id: n.id,
                date_of_action: n.date_of_action,
                subject: n.subject,
              }))}
              onNoteClick={handleTimelineNoteClick}
              onYearChange={handleTimelineYearChange}
            />
          </Box>
        )}
    </Box>
  );
};
